// MobileBottomNav — Iter62b — Dark Premium iOS-style bottom tab bar (5 icons).
// Matches user's HTML template navbar exactly (bg rgba(11,19,48,0.9) + blur + safe-area).
// Replaces the previous blue-heavy bottom nav.
import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Avatar } from './Logo';
import { PushToggle } from './PushToggle';
import {
  Folder, LogOut, TrendingUp, Target, Archive, LayoutGrid, X, ChevronRight, Users as UsersIcon,
} from './icons';

// iOS-style stroke icons (24 viewBox, currentColor) matching the template exactly.
const IconHome = (p) => (
  <svg width={p.size || 22} height={p.size || 22} viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
    <path d="M4 11L12 4L20 11V20H4V11Z" stroke="currentColor" strokeLinejoin="round" />
  </svg>
);
const IconTasks = (p) => (
  <svg width={p.size || 22} height={p.size || 22} viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
    <rect x="4" y="4" width="16" height="16" rx="3" stroke="currentColor" />
    <path d="M8 12L10.5 14.5L16 9" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconNotes = (p) => (
  <svg width={p.size || 22} height={p.size || 22} viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
    <path d="M6 3H18V21L12 17.5L6 21V3Z" stroke="currentColor" strokeLinejoin="round" />
  </svg>
);
const IconMessages = (p) => (
  <svg width={p.size || 22} height={p.size || 22} viewBox="0 0 24 24" fill="none" strokeWidth="1.8">
    <path d="M4 5H20V16H8L4 20V5Z" stroke="currentColor" strokeLinejoin="round" />
  </svg>
);

const TABS = [
  { key: 'home',      label: 'Accueil',    Icon: IconHome,     to: '/',         roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
  { key: 'tasks',     label: 'Tâches',     Icon: IconTasks,    to: '/tasks',    roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
  { key: 'notes',     label: 'Notes',      Icon: IconNotes,    to: '/notes',    roles: ['founder', 'gestionnaire', 'client', 'contributor'] },
  { key: 'messages',  label: 'Messages',   Icon: IconMessages, to: '/messages', roles: ['founder', 'gestionnaire', 'contributor'] },
  { key: 'profile',   label: 'Profil',     Icon: null,         to: null,        roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
];

const T = {
  bg: '#0B1330',
  hairlineSoft: 'rgba(255,255,255,0.05)',
  text: '#F5F7FB',
  textFaint: '#565F84',
  blue: '#3E7BFA',
  blueMid: '#4C8DFF',
  danger: '#F0616B',
};

export function MobileBottomNav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [profileOpen, setProfileOpen] = useState(false);
  const [notesBadge, setNotesBadge] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  const visibleTabs = useMemo(
    () => TABS.filter((t) => t.roles.includes(user?.role) || (t.extraUserIds || []).includes(user?.id)),
    [user]
  );
  const isFounder = user?.role === 'founder';

  useEffect(() => {
    if (!visibleTabs.some((t) => t.to === '/notes')) return;
    let alive = true;
    const fetchBadge = async () => {
      try { const r = await api.get('/notes/badge'); if (alive) setNotesBadge(r.data.count || 0); } catch { /* ignore */ }
    };
    fetchBadge();
    const iv = setInterval(fetchBadge, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [visibleTabs]);

  useEffect(() => {
    if (!isFounder) return;
    let alive = true;
    const fetchPending = async () => {
      try { const r = await api.get('/users/pending'); if (alive) setPendingCount((r.data || []).length); } catch { /* ignore */ }
    };
    fetchPending();
    const iv = setInterval(fetchPending, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [isFounder]);

  const isActive = (to) => {
    if (!to) return false;
    if (to === '/') return loc.pathname === '/';
    return loc.pathname === to || loc.pathname.startsWith(to + '/');
  };

  return (
    <>
      <nav
        data-testid="mobile-bottom-nav"
        style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
          background: 'rgba(11,19,48,0.9)',
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          borderTop: `1px solid ${T.hairlineSoft}`,
          padding: '10px 8px calc(env(safe-area-inset-bottom, 0px) + 12px)',
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          fontFamily: `-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif`,
        }}
      >
        {visibleTabs.map((tab) => {
          const active = tab.key === 'profile' ? profileOpen : isActive(tab.to);
          const Icon = tab.Icon;
          const badge = tab.to === '/notes' ? notesBadge : 0;
          const showPendingDot = tab.key === 'profile' && isFounder && pendingCount > 0;

          return (
            <button
              key={tab.key}
              data-testid={`mobile-bottom-tab-${tab.key}`}
              onClick={() => {
                if (tab.key === 'profile') setProfileOpen(true);
                else { setProfileOpen(false); nav(tab.to); }
              }}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: '2px 4px', background: 'transparent', border: 'none', cursor: 'pointer',
                position: 'relative', flex: 1, minWidth: 0,
                color: active ? T.blue : T.textFaint,
                transition: 'color 0.15s ease',
              }}
            >
              {tab.key === 'profile' ? (
                <div style={{
                  width: 21, height: 21, borderRadius: '50%',
                  background: T.blueMid,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, fontWeight: 700, color: '#fff',
                  overflow: 'hidden',
                }}>
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    (user?.name?.[0] || 'U').toUpperCase()
                  )}
                  {showPendingDot && (
                    <span style={{
                      position: 'absolute', top: -3, right: -6,
                      minWidth: 13, height: 13, borderRadius: 7,
                      background: T.danger, color: '#fff',
                      display: 'grid', placeItems: 'center',
                      fontSize: 8, fontWeight: 700,
                      border: `2px solid ${T.bg}`,
                    }}>{pendingCount > 9 ? '9+' : pendingCount}</span>
                  )}
                </div>
              ) : (
                <div style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
                  <Icon size={22} />
                  {badge > 0 && (
                    <span style={{
                      position: 'absolute', top: -3, right: -8,
                      minWidth: 13, height: 13, borderRadius: 7,
                      background: T.danger, color: '#fff',
                      display: 'grid', placeItems: 'center',
                      fontSize: 8, fontWeight: 700,
                      border: `2px solid ${T.bg}`,
                      padding: '0 3px',
                    }}>{badge > 99 ? '99+' : badge}</span>
                  )}
                </div>
              )}
              <span style={{
                fontSize: 10, fontWeight: 600, letterSpacing: '0.01em',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}>{tab.label}</span>
            </button>
          );
        })}
      </nav>

      {profileOpen && (
        <ProfileSheet
          user={user}
          isFounder={isFounder}
          pendingCount={pendingCount}
          onClose={() => setProfileOpen(false)}
          onNavigate={(to) => { setProfileOpen(false); nav(to); }}
          onLogout={() => {
            localStorage.removeItem('asa_token');
            window.location.replace('/login');
          }}
          logoutSoft={logout}
        />
      )}
    </>
  );
}


function ProfileSheet({ user, isFounder, pendingCount, onClose, onNavigate, onLogout }) {

  // Mêmes accès que la barre latérale ordinateur, selon le rôle (Axel = contributor → Projets, Bibliothèque…).
  const role = user?.role;
  const secondaryLinks = [
    { to: '/projects',       label: 'Projets',         icon: LayoutGrid, roles: ['founder', 'gestionnaire', 'contributor'] },
    { to: '/library',        label: 'Bibliothèque',    icon: Folder,     roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
    { to: '/client-roadmap', label: 'Roadmap client',  icon: Target,     roles: ['founder', 'gestionnaire', 'client'] },
    { to: '/objectives',     label: 'Objectifs',       icon: Target,     roles: ['founder', 'gestionnaire'] },
    { to: '/tracking',       label: 'Tracking',        icon: TrendingUp, roles: ['founder', 'gestionnaire', 'client'] },
    { to: '/clients',        label: 'Clients',         icon: UsersIcon,  roles: ['founder', 'gestionnaire'] },
    { to: '/asa-script',     label: 'ASA Script',      icon: LayoutGrid, roles: ['founder'] },
    { to: '/team',           label: 'Équipe',          icon: UsersIcon,  roles: ['founder', 'gestionnaire'], badge: pendingCount },
    { to: '/archives',       label: 'Archives',        icon: Archive,    roles: ['founder', 'gestionnaire'] },
    { to: '/reglages',       label: 'Réglages',        icon: LayoutGrid, roles: ['founder', 'gestionnaire', 'contributor', 'client'] },
  ].filter((l) => l.roles.includes(role));

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 250,
        background: 'rgba(6,10,18,0.65)', backdropFilter: 'blur(6px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        fontFamily: `-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif`,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#141D42',
          borderTopLeftRadius: 26, borderTopRightRadius: 26,
          padding: '14px 22px calc(env(safe-area-inset-bottom, 0px) + 90px)',
          borderTop: '1px solid rgba(255,255,255,0.09)',
          maxHeight: '92dvh', overflowY: 'auto',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 3, background: 'rgba(255,255,255,0.14)', margin: '0 auto 14px' }} />

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: 14, borderRadius: 16,
          background: '#1C2650', border: '1px solid rgba(255,255,255,0.09)',
          marginBottom: 18,
        }}>
          <Avatar user={user} size={48} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 15, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </div>
            <div style={{ fontSize: 11.5, color: T.textFaint }}>
              {user?.role === 'founder' ? 'Founder' : user?.role === 'gestionnaire' ? 'Gestionnaire' : user?.role === 'client' ? 'Client' : 'Editor'}
              {user?.email ? ` · ${user.email}` : ''}
            </div>
          </div>
          <button
            data-testid="mobile-profile-close"
            onClick={onClose}
            style={{ width: 28, height: 28, borderRadius: 9, background: '#1C2650', border: 'none', color: T.textFaint, cursor: 'pointer' }}
          ><X size={14} /></button>
        </div>

        <div style={{
          padding: '14px 16px', borderRadius: 14,
          background: '#1C2650', border: '1px solid rgba(255,255,255,0.09)',
          marginBottom: 18,
        }}>
          <div style={{ fontWeight: 700, fontSize: 10.5, color: T.textFaint, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Notifications
          </div>
          <PushToggle />
        </div>

        {secondaryLinks.length > 0 && (
          <>
            <div style={{ fontWeight: 700, fontSize: 10.5, color: T.textFaint, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Espace de travail
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {secondaryLinks.map((l) => (
                <button
                  key={l.to}
                  data-testid={`mobile-profile-link-${l.to.replace(/^\//, '').replace(/\//g, '-')}`}
                  onClick={() => onNavigate(l.to)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '13px 16px', borderRadius: 12,
                    background: '#1C2650', border: '1px solid rgba(255,255,255,0.09)',
                    cursor: 'pointer',
                    fontWeight: 600, fontSize: 13.5, color: T.text,
                    textAlign: 'left', fontFamily: 'inherit',
                  }}
                >
                  <l.icon size={16} color={T.textFaint} />
                  <span style={{ flex: 1 }}>{l.label}</span>
                  {l.badge > 0 && (
                    <span style={{
                      minWidth: 19, height: 19, borderRadius: 10,
                      background: T.danger, color: '#fff',
                      display: 'grid', placeItems: 'center',
                      padding: '0 5px', fontWeight: 700, fontSize: 10,
                    }}>{l.badge}</span>
                  )}
                  <ChevronRight size={14} color={T.textFaint} />
                </button>
              ))}
            </div>
          </>
        )}

        <button
          data-testid="mobile-profile-logout"
          onClick={onLogout}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '14px 12px', borderRadius: 14,
            background: 'transparent',
            border: '1.5px solid rgba(240,97,107,0.35)',
            color: T.danger,
            fontWeight: 700, fontSize: 14,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          <LogOut size={15} /> Déconnexion
        </button>
      </div>
    </div>
  );
}
